// Church-local wall-clock strings ("YYYY-MM-DD", "HH:MM") formatted for display.
// Dates are parsed as local calendar dates (never through UTC) so a
// "2026-10-12" service never shows as the 11th west of Greenwich.
import { format } from 'date-fns';

export function parseLocalDate(ymd) {
  if (!ymd || typeof ymd !== 'string') return null;
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function fmtDate(ymd, pattern) {
  const d = parseLocalDate(ymd);
  return d ? format(d, pattern) : '';
}

// The Quick Compose side panel (ServiceDetailFloater) shows the plan date the
// way the Planning Center widget passes it ("Sun, Oct 4"). The Services roster
// carries "YYYY-MM-DD", so it is reformatted before it reaches the modal.
export function toQuickComposePlan(roster) {
  return { ...roster, date: fmtDate(roster?.date, 'EEE, MMM d') || roster?.date || '', direct_roster: true };
}

// "09:00" → "9:00 AM"
export function fmtTime(hhmm) {
  if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return '';
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}

// "Maria Kim" → "Maria K." (the formatName pattern from PlanningCenterWidget.js)
export function formatName(fullName) {
  const parts = (fullName || '').trim().split(' ');
  if (parts.length < 2) return fullName;
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

export function initials(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

export function todayLocal() {
  return format(new Date(), 'yyyy-MM-dd');
}

// "YYYY-MM-DD" + n days (calendar arithmetic, no time zones involved).
export function addDaysYmd(ymd, n) {
  const d = parseLocalDate(ymd);
  if (!d) return ymd;
  d.setDate(d.getDate() + n);
  return format(d, 'yyyy-MM-dd');
}

// The church's wall clock now, as comparable strings (R-EDIT 7 / E-11 use
// church-local time; `tz` comes from the plan's church_timezone).
export function churchNow(tz) {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz || 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = (t) => parts.find((p) => p.type === t)?.value;
    return { date: `${get('year')}-${get('month')}-${get('day')}`, time: `${get('hour')}:${get('minute')}` };
  } catch {
    return { date: todayLocal(), time: format(new Date(), 'HH:mm') };
  }
}

// Section 11.3 (map 1370): the church's zone abbreviation ("CT", "PT"), shown
// next to church-local times only when the device is in a different zone;
// '' when the zones match or the zone is unknown (Fix 6D D6).
export function zoneSuffix(tz) {
  if (!tz) return '';
  try {
    const device = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!device || device === tz) return '';
    const name = (style) => new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: style })
      .formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value || '';
    let abbr = '';
    try { abbr = name('shortGeneric'); } catch { abbr = ''; }
    if (!abbr || /\s/.test(abbr)) abbr = name('short');
    return abbr;
  } catch {
    return '';
  }
}

// True once the church clock reaches `days` after the service start.
export function passedBy(service, days) {
  if (!service?.date) return false;
  const now = churchNow(service.church_timezone);
  const edge = `${addDaysYmd(service.date, days)} ${service.service_time || '00:00'}`;
  return `${now.date} ${now.time}` >= edge;
}

// C13 (U7): "1 song", "2 songs".
export function plural(n, one, many = `${one}s`) {
  const k = Number(n) || 0;
  return `${k} ${k === 1 ? one : many}`;
}
